"use client";

import React from 'react';

import {
  buttonBaseClasses as baseClasses,
  buttonSizeClasses as sizeClasses,
  buttonVariantClasses as variantClasses,
  type ButtonSize,
  type ButtonVariant,
} from './buttonStyles';

export { buttonClassName } from './buttonStyles';
export type { ButtonSize, ButtonVariant };

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  /** `variant` preserves the original API; `intent` is the canonical name. */
  variant?: ButtonVariant;
  intent?: Exclude<ButtonVariant, 'link' | 'ghost' | 'chip' | 'subtle'>;
  size?: ButtonSize;
  loading?: boolean;
  loadingLabel?: string;
};

export function Button({ children, className = '', variant, intent, size = 'md', loading = false, loadingLabel, disabled, type = 'button', ...props }: ButtonProps) {
  const resolvedVariant = intent ?? variant ?? 'primary';
  return (
    <button
      {...props}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`${baseClasses} ${sizeClasses[size]} ${variantClasses[resolvedVariant]} ${className}`}
    >
      {loading ? <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-none" /> : null}
      <span className="inline-flex items-center justify-center gap-2">{loading && loadingLabel ? loadingLabel : children}</span>
    </button>
  );
}

export type IconButtonProps = Omit<ButtonProps, 'children' | 'size'> & {
  label: string;
  children: React.ReactNode;
};

export function IconButton({ label, 'aria-label': ariaLabel, title, ...props }: IconButtonProps) {
  return <Button {...props} size="icon" aria-label={ariaLabel ?? label} title={title ?? label} />;
}
